/**
 * The whole backend, for /demo only.
 *
 * Rather than stubbing each service one by one, this swaps the axios *adapter* on
 * both transports the dashboard uses — the shared `services/api` instance and
 * the bare `axios` default that a few pages (DashboardHome, AnalyticsPage,
 * ReviewsPage, SearchModal) still call directly — so every request is answered
 * from `./fixtures` without a single byte leaving the tab. Nothing 404s, no
 * skeleton hangs, and the network panel stays empty.
 *
 * Mutations resolve as no-ops and raise the "changes are not saved" notice.
 * Install/eject is driven by DemoProvider, so real routes are untouched.
 */

import axios, {
  AxiosHeaders,
  type AxiosAdapter,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import api from '../services/api';
import { emitDemoNotice } from './demoNotice';
import {
  buildDemoBookings,
  buildDemoContentItems,
  buildDemoLeads,
  buildDemoMessageThread,
  buildDemoNotifications,
  buildDemoPrintKit,
  buildDemoReviews,
  buildDemoUser,
  buildDemoWebsite,
  getDemoIdentity,
  whenDemoIdentityReady,
} from './identity';
import {
  DEMO_WEBSITE_ID,
  demoAnalytics,
  demoBookings,
  demoContentItems,
  demoKeywords,
  demoLeads,
  demoNotifications,
  demoPageSpeed,
  demoPeriodDays,
  demoPrintKit,
  demoReviewRequests,
  demoReviews,
  demoSetupStatus,
  demoSubscription,
  demoTimeseries,
  demoUnreadLeadCount,
  demoUnreadNonLeadCount,
  demoUnreadNotificationCount,
  demoUser,
  demoWebsite,
} from './fixtures';

/** The envelope every real route answers in: `{ success, data }`. */
function ok(data: unknown): { success: true; data: unknown } {
  return { success: true, data };
}

interface DemoResult {
  data: unknown;
  status: number;
}

/**
 * Requests fired by the UI on its own (auth bootstrap, marking a lead read as
 * its row scrolls into view) — a toast for those would fire on page load and
 * say nothing useful.
 */
const SILENT_MUTATIONS: RegExp[] = [
  /^\/api\/auth\//,
  /^\/api\/leads\/[^/]+\/view$/,
  /^\/api\/messages\/read$/,
];

function isSilent(path: string): boolean {
  return SILENT_MUTATIONS.some(pattern => pattern.test(path));
}

function parseBody(raw: unknown): Record<string, unknown> {
  if (typeof raw === 'string') {
    try {
      const parsed: unknown = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }
  if (raw && typeof raw === 'object' && !(raw instanceof FormData)) {
    return raw as Record<string, unknown>;
  }
  return {};
}

function limitOf(params: URLSearchParams, fallback: number): number {
  const raw = Number(params.get('limit'));
  return Number.isFinite(raw) && raw > 0 ? raw : fallback;
}

/* ------------------------------------------------------------------ */
/* Downloads                                                           */
/* ------------------------------------------------------------------ */

/**
 * A real, openable one-page PDF that says it is a sample — so the print-kit
 * download button does something honest instead of saving a broken file.
 */
function samplePdf(label: string): Blob {
  const text = `${getDemoIdentity().businessName} - ${label} (demo sample)`;
  const objects = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
    '<< /Type /Page /Parent 2 0 R /MediaBox [0 0 396 216] /Contents 4 0 R /Resources << /Font << /F1 5 0 R >> >> >>',
    null, // filled below — the content stream
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>',
  ];
  const stream = `BT /F1 12 Tf 36 120 Td (${text.replace(/[()\\]/g, '')}) Tj ET`;
  objects[3] = `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`;

  let pdf = '%PDF-1.4\n';
  const offsets: number[] = [];
  objects.forEach((body, index) => {
    offsets.push(pdf.length);
    pdf += `${index + 1} 0 obj\n${body}\nendobj\n`;
  });
  const xrefStart = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.forEach(offset => {
    pdf += `${String(offset).padStart(10, '0')} 00000 n \n`;
  });
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefStart}\n%%EOF\n`;

  return new Blob([pdf], { type: 'application/pdf' });
}

function leadsCsv(): Blob {
  const header = 'Name,Email,Phone,Service,Source,Status,Created\n';
  const rows = buildDemoLeads()
    .map(lead =>
      [
        lead.name,
        lead.email ?? '',
        lead.phone ?? '',
        lead.service ?? '',
        lead.source,
        lead.status,
        lead.createdAt,
      ].join(',')
    )
    .join('\n');
  return new Blob([header + rows], { type: 'text/csv' });
}

/* ------------------------------------------------------------------ */
/* Reads                                                               */
/* ------------------------------------------------------------------ */

function searchResults(query: string): unknown {
  const needle = query.trim().toLowerCase();
  const hit = (value: string | null | undefined) =>
    Boolean(value && value.toLowerCase().includes(needle));

  return {
    leads: buildDemoLeads()
      .filter(lead => hit(lead.name) || hit(lead.email) || hit(lead.service) || hit(lead.message))
      .slice(0, 5)
      .map(lead => ({
        id: lead.id,
        name: lead.name,
        email: lead.email ?? null,
        status: lead.status,
        source: lead.source,
        notes: lead.notes ?? null,
        websiteId: lead.websiteId,
      })),
    reviews: buildDemoReviews()
      .filter(review => hit(review.reviewerName) || hit(review.reviewText))
      .slice(0, 5)
      .map(review => ({
        id: review.id,
        reviewerName: review.reviewerName,
        reviewText: review.reviewText,
        rating: review.rating,
        platform: review.platform,
        websiteId: review.websiteId,
      })),
    websites: [buildDemoWebsite()]
      .filter(site => hit(site.domain) || hit(site.businessName))
      .map(site => ({
        id: site.id,
        domain: site.domain,
        businessName: site.businessName ?? null,
        status: site.status,
      })),
    bookings: buildDemoBookings().filter(
      booking => hit(booking.customerName) || hit(booking.serviceType)
    ),
  };
}

function resolveGet(path: string, params: URLSearchParams): DemoResult | null {
  if (path === '/api/auth/profile') return { data: ok({ user: buildDemoUser() }), status: 200 };

  if (path === '/api/websites') return { data: ok({ websites: [buildDemoWebsite()] }), status: 200 };

  if (path === '/api/setup/status') return { data: ok(demoSetupStatus), status: 200 };

  if (path === '/api/account/trial-status') {
    return {
      data: ok({ status: 'active', hasSubscription: true, trialEndsAt: null }),
      status: 200,
    };
  }

  if (path === '/api/payment/subscription') {
    // Two readers, two shapes: billingService unwraps `.subscription`, the
    // setup-status fallback reads the object itself. Answer both at once.
    return { data: ok({ subscription: demoSubscription, ...demoSubscription }), status: 200 };
  }

  if (path === '/api/payment/billing-history') {
    return { data: ok({ invoices: [] }), status: 200 };
  }

  if (path === '/api/leads') {
    const status = params.get('status');
    const source = params.get('source');
    const allLeads = buildDemoLeads();
    const leads = allLeads
      .filter(lead => (status ? lead.status === status : true))
      .filter(lead => (source ? lead.source === source : true))
      .slice(0, limitOf(params, allLeads.length));
    return { data: ok({ leads, total: leads.length }), status: 200 };
  }

  if (path === '/api/leads/unread-count') {
    return { data: ok({ count: demoUnreadLeadCount }), status: 200 };
  }

  if (path === '/api/bookings') {
    const status = params.get('status');
    const bookings = buildDemoBookings().filter(booking => (status ? booking.status === status : true));
    return { data: ok({ bookings, total: bookings.length }), status: 200 };
  }

  if (path === '/api/reviews') {
    const platform = params.get('platform');
    const rating = params.get('rating');
    const responded = params.get('responded');
    const reviews = buildDemoReviews()
      .filter(review => (platform ? review.platform === platform : true))
      .filter(review => (rating ? review.rating === Number(rating) : true))
      .filter(review =>
        responded === null ? true : review.responded === (responded === 'true')
      )
      .slice(0, limitOf(params, demoReviews.length));
    return { data: ok({ reviews, total: reviews.length }), status: 200 };
  }

  if (path === '/api/reviews/requests') {
    return { data: ok({ requests: demoReviewRequests, total: demoReviewRequests.length }), status: 200 };
  }

  if (path === '/api/notifications') {
    const unreadOnly = params.get('unread') === 'true';
    const notifications = buildDemoNotifications()
      .filter(notification => (unreadOnly ? !notification.read : true))
      .slice(0, limitOf(params, 10));
    return { data: ok({ notifications }), status: 200 };
  }

  if (path === '/api/notifications/unread-count') {
    const type = params.get('type');
    const excludeType = params.get('excludeType');
    const count = type
      ? demoNotifications.filter(n => !n.read && n.type === type).length
      : excludeType
        ? demoUnreadNonLeadCount
        : demoUnreadNotificationCount;
    return { data: ok({ count }), status: 200 };
  }

  if (path === '/api/content-plan') {
    const status = params.get('status');
    const type = params.get('type');
    const items = buildDemoContentItems()
      .filter(item => (status ? item.status === status : true))
      .filter(item => (type ? item.type === type : true));
    return { data: ok({ items, total: items.length }), status: 200 };
  }

  if (path.startsWith('/api/content-plan/')) {
    const id = path.split('/').pop();
    const items = buildDemoContentItems();
    const item = items.find(entry => entry.id === id) ?? items[0];
    return { data: ok(item), status: 200 };
  }

  if (path === '/api/print-kit') return { data: ok(buildDemoPrintKit()), status: 200 };

  if (path === '/api/messages/thread') return { data: ok(buildDemoMessageThread()), status: 200 };

  if (path === '/api/search') {
    return { data: ok(searchResults(params.get('q') ?? '')), status: 200 };
  }

  if (path === '/api/settings') {
    return {
      data: ok({
        user: buildDemoUser(),
        notifications: { emailLeads: true, emailReviews: true, emailBilling: true },
      }),
      status: 200,
    };
  }

  if (path === '/api/activity') {
    return { data: ok({ activities: [], total: 0 }), status: 200 };
  }

  // /api/websites/:id and everything hanging off it
  const websiteMatch = /^\/api\/websites\/([^/]+)(\/.*)?$/.exec(path);
  if (websiteMatch) {
    const rest = websiteMatch[2] ?? '';
    const days = demoPeriodDays(params.get('period'));

    if (rest === '') return { data: ok({ website: buildDemoWebsite() }), status: 200 };
    if (rest === '/deployment') {
      return {
        data: ok({
          deployment: {
            id: 'demo-deployment-1',
            status: 'ready',
            detail: 'Published',
            createdAt: demoWebsite.lastUpdated,
            updatedAt: demoWebsite.lastUpdated,
          },
        }),
        status: 200,
      };
    }
    if (rest === '/analytics') {
      return { data: ok({ websiteStats: demoAnalytics(days) }), status: 200 };
    }
    if (rest === '/analytics/pageviews') {
      return { data: ok(demoTimeseries(days)), status: 200 };
    }
    if (rest === '/seo/keywords') return { data: ok(demoKeywords), status: 200 };
    if (rest === '/seo/pagespeed') return { data: ok(demoPageSpeed), status: 200 };
  }

  return null;
}

/* ------------------------------------------------------------------ */
/* Writes — accepted, never kept                                       */
/* ------------------------------------------------------------------ */

function resolveWrite(method: string, path: string, body: Record<string, unknown>): DemoResult {
  if (path === '/api/auth/refresh-token') {
    return { data: ok({ token: 'demo-token', user: demoUser }), status: 200 };
  }
  if (path === '/api/auth/logout') return { data: ok({}), status: 200 };

  const leadMatch = /^\/api\/leads\/([^/]+)(\/view)?$/.exec(path);
  if (leadMatch) {
    const lead = demoLeads.find(entry => entry.id === leadMatch[1]) ?? demoLeads[0];
    return { data: ok(method === 'delete' ? {} : { ...lead, ...body }), status: 200 };
  }
  if (path === '/api/leads') {
    return {
      data: ok({
        ...demoLeads[0],
        ...body,
        id: `demo-lead-${Date.now()}`,
        websiteId: DEMO_WEBSITE_ID,
        createdAt: new Date().toISOString(),
      }),
      status: 200,
    };
  }

  const bookingMatch = /^\/api\/bookings\/([^/]+)$/.exec(path);
  if (bookingMatch) {
    const booking = demoBookings.find(entry => entry.id === bookingMatch[1]) ?? demoBookings[0];
    return { data: ok(method === 'delete' ? {} : { ...booking, ...body }), status: 200 };
  }
  if (path === '/api/bookings') {
    return {
      data: ok({ ...demoBookings[0], ...body, id: `demo-booking-${Date.now()}` }),
      status: 200,
    };
  }

  const contentMatch = /^\/api\/content-plan\/([^/]+)$/.exec(path);
  if (contentMatch) {
    const item = demoContentItems.find(entry => entry.id === contentMatch[1]) ?? demoContentItems[0];
    return { data: ok(method === 'delete' ? {} : { ...item, ...body }), status: 200 };
  }
  if (path === '/api/content-plan') {
    return {
      data: ok({ ...demoContentItems[0], ...body, id: `demo-content-${Date.now()}` }),
      status: 200,
    };
  }

  const reviewMatch = /^\/api\/reviews\/([^/]+)$/.exec(path);
  if (reviewMatch && reviewMatch[1] !== 'requests' && reviewMatch[1] !== 'sync-google') {
    const review = demoReviews.find(entry => entry.id === reviewMatch[1]) ?? demoReviews[0];
    return { data: ok({ ...review, ...body }), status: 200 };
  }
  if (path === '/api/reviews/sync-google') {
    return { data: ok({ created: 0, skipped: demoReviews.length }), status: 200 };
  }
  if (path === '/api/reviews/requests') {
    return {
      data: ok({ ...demoReviewRequests[0], ...body, id: `demo-request-${Date.now()}` }),
      status: 200,
    };
  }

  if (path === '/api/print-kit/regenerate') {
    return { data: ok({ status: demoPrintKit.status }), status: 200 };
  }

  if (path === '/api/messages') {
    return {
      data: ok({
        id: `demo-message-${Date.now()}`,
        sender: 'customer',
        body: String(body.body ?? ''),
        createdAt: new Date().toISOString(),
        readAt: null,
        deliveredAt: null,
        deliveryError: null,
      }),
      status: 200,
    };
  }

  return { data: ok({}), status: 200 };
}

/* ------------------------------------------------------------------ */
/* Adapter                                                             */
/* ------------------------------------------------------------------ */

function resolve(config: InternalAxiosRequestConfig): DemoResult {
  const method = (config.method ?? 'get').toLowerCase();
  const [path, search = ''] = (config.url ?? '').split('?');
  const params = new URLSearchParams(search);

  if (config.responseType === 'blob') {
    const piece = /^\/api\/print-kit\/piece\/([^/]+)$/.exec(path);
    if (piece) return { data: samplePdf(piece[1]), status: 200 };
    return { data: leadsCsv(), status: 200 };
  }

  if (method === 'get') {
    return resolveGet(path, params) ?? { data: ok({}), status: 200 };
  }

  if (!isSilent(path)) emitDemoNotice();
  return resolveWrite(method, path, parseBody(config.data));
}

const demoAdapter: AxiosAdapter = async config => {
  // Never answer with the default business while `?from=` is still resolving.
  await whenDemoIdentityReady();
  const { data, status } = resolve(config);
  // A beat of latency, so loading states are exercised rather than skipped.
  await new Promise(done => window.setTimeout(done, 60));
  return {
    data,
    status,
    statusText: 'OK',
    headers: new AxiosHeaders(),
    config,
  } as AxiosResponse;
};

let installed = false;
let apiInterceptorId: number | null = null;
let axiosInterceptorId: number | null = null;

const attach = (config: InternalAxiosRequestConfig) => {
  config.adapter = demoAdapter;
  return config;
};

/** Idempotent: safe to call from render and again from an effect. */
export function installDemoApi(): void {
  if (installed) return;
  installed = true;
  apiInterceptorId = api.interceptors.request.use(attach);
  axiosInterceptorId = axios.interceptors.request.use(attach);
}

export function uninstallDemoApi(): void {
  if (!installed) return;
  installed = false;
  if (apiInterceptorId !== null) api.interceptors.request.eject(apiInterceptorId);
  if (axiosInterceptorId !== null) axios.interceptors.request.eject(axiosInterceptorId);
  apiInterceptorId = null;
  axiosInterceptorId = null;
}
